import React, { useEffect, useMemo, useState } from "react";
import { useApp } from "../context/AppContext";
import { fmtMoney } from "../lib/appUtils";
import { AlertTriangle, BellRing } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { ink } from "../lib/ink";

const SEV_TINT = { high: "#b91c1c", medium: "#b45309", low: "#4b5b75" };

/**
 * CollectionsWorkbench —— 逾期回款催收面板。
 *
 * 展示需求（2026-10-21）：财务报表取消弹窗，3 个收款图表由 CollectionsChartsPanel
 * 在「项目财务」顶部展示。本组件仍只负责下方的逾期催收面板（带「一键催收」操作），
 * 复用同一个分公司筛选，避免在工作台重复渲染统计图表。
 *
 * props：
 *   - branchFilter：当前选中的分公司标签（来自财务页分公司 chip）
 *   - branchOf：id → 分公司标签 映射，用于按项目 id 判断归属分公司
 */
export default function CollectionsWorkbench({ branchFilter, branchOf }) {
  const { lang, visibleProjects, api, user } = useApp();
  const zh = lang === "zh";
  const nav = useNavigate();
  const [overdue, setOverdue] = useState([]);
  const [reminding, setReminding] = useState(false);
  const canRemind = ["admin", "finance", "ceo"].includes(user?.role);

  useEffect(() => {
    api.get("/collections/overdue").then(({ data }) => setOverdue(data)).catch(() => {});
  }, [api, visibleProjects.length]);

  const inBranch = (pid) => !branchFilter || (branchOf?.[pid] === branchFilter);
  const overdueList = useMemo(() => overdue.filter((it) => inBranch(it.projectId)), [overdue, branchFilter, branchOf]); // eslint-disable-line

  // 一键催收：扫描逾期款项并按严重度生成催收提醒（含财务与对应销售）
  const sendCollectionReminders = async () => {
    setReminding(true);
    try {
      const { data } = await api.post("/collections/remind", { minDays: 14 });
      alert(zh ? `已扫描 ${data.scanned} 笔逾期款项，新生成 ${data.created} 条催收提醒（含财务与对应销售）。` : `Scanned ${data.scanned}; created ${data.created} collection reminders.`);
    } catch (e) { alert(e?.response?.data?.detail || e.message); }
    setReminding(false);
  };

  return (
    <div className="space-y-5" data-testid="collections-workbench">
      {/* overdue collections panel */}
      {overdueList.length > 0 && (
        <div className="bg-white rounded-xl border border-red-200 overflow-hidden" data-testid="overdue-panel">
          <div className="flex items-center gap-2 px-5 py-3 border-b border-red-100 bg-red-50/60">
            <AlertTriangle className="h-4 w-4 text-red-600" />
            <h3 className="font-semibold text-slate-900">{zh ? "逾期回款催收" : "Overdue Collections"}</h3>
            <span className="text-xs text-red-600 font-medium">{overdueList.length} {zh ? "笔逾期" : "overdue"}</span>
            {canRemind && (
              <button data-testid="send-collection-reminders-btn" onClick={sendCollectionReminders} disabled={reminding}
                className="ml-auto inline-flex items-center gap-1 text-xs text-white bg-brand-500 hover:bg-brand-600 disabled:opacity-50 rounded-lg px-2.5 py-1.5 transition-colors">
                <BellRing className="h-3.5 w-3.5" />{reminding ? (zh ? "发送中…" : "Sending…") : (zh ? "一键催收(≥14天)" : "Send Reminders (≥14d)")}
              </button>
            )}
          </div>
          <div className="overflow-x-auto max-h-64 overflow-y-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-500 sticky top-0"><tr>
                <th className="text-left font-medium px-5 py-2">{zh ? "项目" : "Project"}</th>
                <th className="text-left font-medium px-4 py-2">{zh ? "销售" : "Sales"}</th>
                <th className="text-left font-medium px-4 py-2">{zh ? "节点" : "Milestone"}</th>
                <th className="text-right font-medium px-4 py-2">{zh ? "应收" : "Amount"}</th>
                <th className="text-right font-medium px-4 py-2">{zh ? "逾期" : "Overdue"}</th>
              </tr></thead>
              <tbody>
                {overdueList.slice(0, 20).map((it, i) => (
                  <tr key={i} className="border-t border-slate-100" data-testid={`overdue-row-${i}`}>
                    <td className="px-5 py-2">
                      <button data-testid={`overdue-addr-${it.projectId}-${i}`} onClick={() => nav(`/finance/project/${it.projectId}`)} className="text-left group">
                        <p className="font-medium text-slate-800 max-w-[220px] truncate group-hover:text-brand-500 group-hover:underline">{it.address}</p>
                        <p className="text-[11px] text-slate-400">{it.clientName} · {it.teamName}</p>
                      </button>
                    </td>
                    <td className="px-4 py-2 text-slate-600">{it.sales || "—"}</td>
                    <td className="px-4 py-2 text-slate-500">{zh ? it.stageLabel.split(" ")[0] : it.stageLabel}</td>
                    <td className="px-4 py-2 text-right font-medium text-slate-900">{fmtMoney(it.amount)}</td>
                    <td className="px-4 py-2 text-right"><span className="inline-block text-xs font-semibold px-2 py-0.5 rounded-full" style={ink(SEV_TINT[it.severity], SEV_TINT[it.severity] + "22")}>{it.overdueDays} {zh ? "天" : "d"}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
